import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBrowserTracking } from "./useBrowserTracking";
import { useIngestEvent } from "../api-data/reporting/events";
import { EventType } from "../api-data/reporting/enums";
import { TrackingMetadata } from "../api-data/reporting/types";
import { collectBrowserInfo } from "../utils/browserInfo";

/**
 * Generic Event Tracking Hook
 *
 * Provides a simple trackEvent function that automatically injects
 * user, session, and device metadata.
 */
export const useTrackEvent = () => {
  const { user, isAuthenticated } = useAuth();
  const { sessionId } = useBrowserTracking();
  const location = useLocation();
  const { mutate: ingestEvent } = useIngestEvent();

  // Memoize browser info to avoid re-calculating on every render
  const browserInfo = useMemo(() => collectBrowserInfo(), []);

  /**
   * Builds the standardized metadata payload
   */
  const buildMetadata = useCallback((): TrackingMetadata => {
    // Get sessionId from sessionStorage with key "browserSessionId"
    const browserSessionId =
      sessionStorage.getItem("browserSessionId") || undefined;

    return {
      userId: isAuthenticated ? (user?.id ?? null) : null,
      sessionId: sessionId || browserSessionId || null,
      isLoggedIn: isAuthenticated,
      timestamp: new Date().toISOString(),
      platform: "WEB",
      route: location.pathname + location.search,
      appVersion: process.env.REACT_APP_VERSION || "unknown",
      deviceType: browserInfo.deviceType,
      deviceModel: browserInfo.deviceModel,
      os: browserInfo.os,
      osVersion: browserInfo.osVersion,
      browserName: browserInfo.browserName,
      browserVersion: browserInfo.browserVersion,
      userAgent: browserInfo.userAgent,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
    };
  }, [
    user?.id,
    isAuthenticated,
    sessionId,
    location.pathname,
    location.search,
    browserInfo,
  ]);

  /**
   * Tracks an event with the given name and payload
   *
   * @param eventName - The type of event (from EventType enum or string)
   * @param payload - Business logic data associated with the event
   */
  const trackEvent = useCallback(
    (eventName: EventType | string, payload: Record<string, any> = {}) => {
      const metadata = buildMetadata();

      const request = {
        eventType: eventName,
        source: "WEB",
        payload,
        metadata,
      };

      console.debug(`[Tracking] ${eventName}`, request);

      ingestEvent(request);
    },
    [buildMetadata, ingestEvent],
  );

  return { trackEvent };
};
